import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getNotifications, NotificationsRead } from "../../../api";
import { getPublisherAvatarUrl } from "../../../utils";
import publisherStore from "../../../flux/publisherStore";
import { getPublishers } from "../../../flux/publisherAction";
import { HomepageThemeScope } from "../styles";
import {
  NotifAvatar,
  NotifAvatarFallback,
  NotifBadge,
  NotifEmpty,
  NotifEmptyDesc,
  NotifEmptyIcon,
  NotifEmptyTitle,
  NotifFooter,
  NotifFooterButton,
  NotifItemBody,
  NotifItemButton,
  NotifItemDate,
  NotifItemText,
  NotifList,
  NotifMetaRow,
  NotifPanel,
  NotifPanelHeader,
  NotifPanelTitle,
  NotifPanelUnread,
  NotifRoot,
  NotifScrim,
  NotifScroll,
  NotifTrigger,
  NotifTypeChip,
  NotifUnreadDot,
} from "./HeaderNotificationsMenu.styles";

/** Same cadence as the previous header bell. */
const NOTIFICATION_POLL_MS = 60000;

/** Lovable DropdownMenuContent sideOffset={10} */
const SIDE_OFFSET = 10;
const VIEWPORT_GUTTER = 12;
/** Lovable w-[22rem] */
const PANEL_WIDTH_PX = 22 * 16;

const TYPE_META = {
  1: { label: "Alert", key: "alert", verb: "shared a new alert" },
  2: { label: "Update", key: "update", verb: "updated an alert" },
  3: { label: "Post", key: "post", verb: "published a new post" },
  4: { label: "Reply", key: "reply", verb: "replied to your comment" },
};

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const BellOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5" />
    <path d="M17 17H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <path d="m2 2 20 20" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const initialsOf = (name = "") =>
  name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const resolveTypeMeta = (type) =>
  TYPE_META[type] || { label: "Update", key: "update", verb: "sent an update" };

const resolveLink = (post) => {
  if (post.type === 1 || post.type === 2) {
    return post.alertId ? `/alerts/${post.alertId}` : "/alerts";
  }
  if (post.type === 3) {
    return post.url || "#";
  }
  if (post.type === 4) {
    return post.post?.url || "#";
  }
  return "#";
};

/** Lovable date-fns: format(date, "MMM d, yyyy · h:mm a") */
const formatNotifDate = (date) => {
  if (!date) return null;
  try {
    const raw = String(date);
    const parsed = new Date(
      raw.includes("T") || /Z$/i.test(raw) ? raw : `${raw} UTC`
    );
    if (Number.isNaN(parsed.getTime())) return null;
    return dayjs(parsed).format("MMM D, YYYY · h:mm a");
  } catch {
    return null;
  }
};

/**
 * Lovable NotificationsMenu UX (DropdownMenu portal + modal scroll lock)
 * + prod getNotifications / NotificationsRead behavior.
 */
const notifyNewItems = (list) => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  list.forEach((item) => {
    if (item?.consumed !== false || item?.id == null) return;
    const publisher = item.author || item.publisherOrStaff;
    const name = publisher?.name || "An analyst";
    const typeMeta = resolveTypeMeta(item.type);
    try {
      const note = new Notification("New update on Bitcoin.live", {
        body: `${name} ${typeMeta.verb}`,
        tag: `bl-notif-${item.id}`,
      });
      note.onclick = () => {
        window.focus();
        note.close();
      };
    } catch {
      /* Browser can reject the popup when notifications are blocked. */
    }
  });
};

const HeaderNotificationsMenu = () => {
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const primedRef = useRef(false);
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [items, setItems] = useState([]);
  const [publishers, setPublishers] = useState(publisherStore.getPublishers());

  openRef.current = open;

  const unread = items.filter((n) => n.consumed === false).length;
  const badge = unread > 99 ? "99+" : String(unread);

  const refreshRef = useRef(async () => {});
  refreshRef.current = async ({ notify = false } = {}) => {
    try {
      const resp = await getNotifications();
      if (!resp?.data) return;
      const next = Array.isArray(resp.data) ? resp.data : [];
      const fresh = next.filter(
        (item) =>
          item?.consumed === false &&
          item?.id != null &&
          !seenIdsRef.current.has(item.id)
      );
      next.forEach((item) => {
        if (item?.id != null) seenIdsRef.current.add(item.id);
      });
      if (notify && primedRef.current) notifyNewItems(fresh);
      primedRef.current = true;
      setLoaded(true);
      setItems((prev) => {
        if (!openRef.current) return next;
        const prevById = new Map(prev.map((item) => [item.id, item]));
        return next.map((item) => {
          const previous = prevById.get(item.id);
          if (previous && previous.consumed === false) {
            return { ...item, consumed: false };
          }
          return item;
        });
      });
    } catch {
      setLoaded(true);
    }
  };

  useEffect(() => {
    refreshRef.current({ notify: false });
    const pollId = window.setInterval(() => {
      refreshRef.current({ notify: true });
    }, NOTIFICATION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshRef.current({ notify: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }
    const onPublishers = () => setPublishers(publisherStore.getPublishers());
    publisherStore.addChangeListener(onPublishers);
    return () => {
      window.clearInterval(pollId);
      document.removeEventListener("visibilitychange", onVisible);
      publisherStore.removeChangeListener(onPublishers);
    };
  }, []);

  const getAvatarUrl = (publisher) => {
    if (!publisher?.id || !publishers?.length) {
      return publisher ? getPublisherAvatarUrl(publisher) : "";
    }
    const match = publishers.find((p) => p.id === publisher.id);
    return getPublisherAvatarUrl(match || publisher);
  };

  const closePanel = () => {
    openRef.current = false;
    setOpen(false);
    setItems((prev) => prev.map((n) => ({ ...n, consumed: true })));
    refreshRef.current({ notify: false });
  };

  /**
   * Lovable: align="end" + sideOffset={10} + collision padding ~12px
   * so the 22rem panel stays fully on-screen on mobile.
   */
  const updatePanelPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const top = Math.round(rect.bottom + SIDE_OFFSET);
    const width = Math.min(
      PANEL_WIDTH_PX,
      window.innerWidth - VIEWPORT_GUTTER * 2
    );

    // align end: right edge of panel = right edge of trigger
    let left = Math.round(rect.right - width);
    const maxLeft = window.innerWidth - VIEWPORT_GUTTER - width;
    left = Math.min(Math.max(left, VIEWPORT_GUTTER), Math.max(maxLeft, VIEWPORT_GUTTER));

    setPanelStyle({
      top,
      left,
      width,
      maxHeight: `calc(100vh - ${top + VIEWPORT_GUTTER}px)`,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") closePanel();
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closePanel();
    };
    // Match Radix modal: block page touch-scroll; allow list scroll only.
    const onTouchMove = (event) => {
      const list = panelRef.current?.querySelector("[data-notif-scroll]");
      if (
        list &&
        list.contains(event.target) &&
        list.scrollHeight > list.clientHeight + 1
      ) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [open]);

  const handleToggle = () => {
    if (open) {
      closePanel();
      return;
    }
    openRef.current = true;
    setOpen(true);
    refreshRef.current({ notify: false });
    // Prod: mark all read on open. Keep local unread styling until close.
    NotificationsRead().catch(() => {});
  };

  const openItem = (post) => {
    const to = resolveLink(post);
    closePanel();
    if (to && to !== "#") {
      navigate(to);
    }
  };

  const openSettings = () => {
    closePanel();
    navigate("/user-profile?tab=notifications");
  };

  const panel =
    open && panelStyle
      ? createPortal(
          <HomepageThemeScope>
            <NotifScrim aria-hidden="true" onClick={closePanel} />
            <NotifPanel ref={panelRef} role="menu" style={panelStyle}>
              <NotifPanelHeader>
                <NotifPanelTitle>Notifications</NotifPanelTitle>
                {unread > 0 && (
                  <NotifPanelUnread>{badge} unread</NotifPanelUnread>
                )}
              </NotifPanelHeader>

              <NotifScroll data-notif-scroll>
                {!loaded ? (
                  <NotifEmpty>
                    <NotifEmptyTitle>Loading notifications…</NotifEmptyTitle>
                  </NotifEmpty>
                ) : items.length === 0 ? (
                  <NotifEmpty>
                    <NotifEmptyIcon>
                      <BellOffIcon />
                    </NotifEmptyIcon>
                    <NotifEmptyTitle>No notifications yet</NotifEmptyTitle>
                    <NotifEmptyDesc>
                      New analyst posts, alerts and replies will show up here.
                    </NotifEmptyDesc>
                  </NotifEmpty>
                ) : (
                  <NotifList>
                    {items.map((post, idx) => {
                      const publisher = post.author || post.publisherOrStaff;
                      const typeMeta = resolveTypeMeta(post.type);
                      const avatarUrl = getAvatarUrl(publisher);
                      const name = publisher?.name || "";
                      const dateLabel = formatNotifDate(
                        post.publishedAt || post.createdAt
                      );
                      const unreadItem = post.consumed === false;

                      return (
                        <li key={post.id || idx}>
                          <NotifItemButton
                            type="button"
                            $unread={unreadItem}
                            onClick={() => openItem(post)}
                          >
                            {avatarUrl ? (
                              <NotifAvatar src={avatarUrl} alt="" />
                            ) : (
                              <NotifAvatarFallback>
                                {initialsOf(name) || "?"}
                              </NotifAvatarFallback>
                            )}
                            <NotifItemBody>
                              <NotifMetaRow>
                                <NotifTypeChip $type={typeMeta.key}>
                                  {typeMeta.label}
                                </NotifTypeChip>
                                {unreadItem && <NotifUnreadDot />}
                              </NotifMetaRow>
                              <NotifItemText>
                                {name ? (
                                  <>
                                    <span className="notif-author">{name}</span>{" "}
                                    <span className="notif-action">
                                      {typeMeta.verb}
                                    </span>
                                  </>
                                ) : (
                                  <span className="notif-action">
                                    New {typeMeta.label.toLowerCase()}
                                  </span>
                                )}
                              </NotifItemText>
                              {dateLabel && (
                                <NotifItemDate>{dateLabel}</NotifItemDate>
                              )}
                            </NotifItemBody>
                          </NotifItemButton>
                        </li>
                      );
                    })}
                  </NotifList>
                )}
              </NotifScroll>

              <NotifFooter>
                <NotifFooterButton type="button" onClick={openSettings}>
                  <SettingsIcon />
                  Notification settings
                </NotifFooterButton>
              </NotifFooter>
            </NotifPanel>
          </HomepageThemeScope>,
          document.body
        )
      : null;

  return (
    <NotifRoot>
      <NotifTrigger
        ref={triggerRef}
        type="button"
        aria-label={
          unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
        }
        aria-expanded={open}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <BellIcon />
        {unread > 0 && <NotifBadge>{badge}</NotifBadge>}
      </NotifTrigger>
      {panel}
    </NotifRoot>
  );
};

export default HeaderNotificationsMenu;
